import { useState } from 'react';
import i18n from 'helpers/i18n';
import messages from './messages';
import { Button, Text, TextLink } from '@teachstonellc/component-library';
import MobileForm from 'components/Phone/MobileForm';
import SmsCodeForm from 'components/Phone/SmsCodeForm';
import { updateUserMeSettings } from 'actions/userMe';

import {
  TextWrapper,
  TermsWrapper,
  SuccessWrapper,
  ButtonsWrapper
} from './Styled';

type PhoneSetupSteps = 'enterPhone' | 'enterCode' | 'setupCompleted';

interface PhonePromptProps {
  /** Callback fired when users complete setting up their phone number or dismiss the prompt. */
  onSuccess: () => void;

  /** Heading element for the step title. Defaults to `h2`. */
  headingAs?: 'h1' | 'h2';
}

function PhonePrompt({ onSuccess, headingAs = 'h2' }: PhonePromptProps) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [step, setStep] = useState<PhoneSetupSteps>('enterPhone');

  function handleSuccess(newPhoneNumber: string) {
    setPhoneNumber(newPhoneNumber);
    setStep('enterCode');
  }

  function handleVerification() {
    setStep('setupCompleted');
  }

  function handleDismiss() {
    onSuccess();
  }

  async function handlePermanentDismiss() {
    await updateUserMeSettings({
      user_setting: {
        dismiss_login_phone_prompt: true
      }
    });
    onSuccess();
  }

  if (step === 'setupCompleted') {
    return (
      <>
        <Text as={headingAs} variant="h2" className="text-center pb-4">
          {i18n.ft(messages.success)}
        </Text>
        <TextWrapper>
          <div>{i18n.ft(messages.successSubtitle1, { phoneNumber })}</div>
          <div>{i18n.ft(messages.successSubtitle2)}</div>
        </TextWrapper>

        <SuccessWrapper>
          <Button variant="primary" onClick={onSuccess}>
            {i18n.ft(messages.continue)}
          </Button>
        </SuccessWrapper>
      </>
    );
  }

  return (
    <>
      {step === 'enterCode' ? (
        <>
          <Text as={headingAs} variant="h2" className="text-center pb-4">
            {i18n.ft(messages.enterCode)}
          </Text>
          <TextWrapper>
            <div>{i18n.ft(messages.enterCodeSubtitle1, { phoneNumber })}</div>
            <div>{i18n.ft(messages.enterCodeSubtitle2)}</div>
          </TextWrapper>
          <SmsCodeForm onSucess={handleVerification} />
        </>
      ) : (
        <>
          <Text as={headingAs} variant="h2" className="text-center pb-4">
            {i18n.ft(messages.enterPhone)}
          </Text>
          <TextWrapper>
            {i18n.ft(messages.enterPhoneSubtitle) + ' '}
            <TextLink
              href="https://teachstone.com/support/login/#link-1"
              target="_blank"
              rel="noreferrer"
              className="[&>span]:text-[1rem] [&>span]:font-[proxima-nova]"
            >
              {i18n.ft(messages.learnMore)}
            </TextLink>
          </TextWrapper>
          <MobileForm isVerifying={false} onSuccess={handleSuccess} />
        </>
      )}

      <ButtonsWrapper>
        <TextLink
          as="button"
          type="button"
          onClick={handleDismiss}
          className="my-2 [&>span]:text-[1rem] [&>span]:font-[proxima-nova]"
        >
          {i18n.ft(messages.nextTime)}
        </TextLink>
        <TextLink
          as="button"
          type="button"
          onClick={handlePermanentDismiss}
          className="[&>span]:text-[1rem] [&>span]:font-[proxima-nova]"
        >
          {i18n.ft(messages.dontAskAgain)}
        </TextLink>
      </ButtonsWrapper>

      <TermsWrapper>{i18n.ft(messages.footer)}</TermsWrapper>
    </>
  );
}
export default PhonePrompt;
